import React, { useMemo } from 'react';
import * as S from './styles';
import {
  faEraser,
  faPlay,
  faImage,
} from '@fortawesome/free-solid-svg-icons';
import { DateInput } from '../../../components/interface/DateInput';
import Table from '../../../components/interface/Tabela';
import ActionButtons from '../../../components/interface/ActionButtons';
import { Modal } from '../../../components/interface/Modal';
import { Despesa, RelatoriosDespesasViewProps, StatusDespesa } from './types';
import MultiSelect, { MultiSelectOption } from './MultiSelect';
import { DefaultTheme, useTheme } from 'styled-components';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

const STATUS_OPTIONS: MultiSelectOption[] = [
  { value: 'PENDENTE', label: 'Pendente' },
  { value: 'APROVADA', label: 'Aprovada' },
  { value: 'REJEITADA', label: 'Rejeitada' },
  { value: 'CANCELADA', label: 'Cancelada' },
];

const CATEGORIA_COLORS = [
  '#4e79a7',
  '#f28e2c',
  '#e15759',
  '#76b7b2',
  '#59a14f',
  '#edc949',
  '#af7aa1',
  '#ff9da7',
  '#9c755f',
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);

const RelatoriosDespesasContent: React.FC<RelatoriosDespesasViewProps> = ({
  despesas,
  categorias,
  entregadores,
  loading,
  error,
  formData,
  selectedDespesa,
  showModal,
  handleInputChange,
  handleSearch,
  handleClear,
  handleViewImage,
  closeModal,
}) => {
  const theme = useTheme();
  const columns = [
    {
      key: 'codigoDespesa' as keyof Despesa,
      header: 'Código',
      width: '90px',
      filterable: true,
    },
    {
      key: 'dataDespesa' as keyof Despesa,
      header: 'Data',
      filterable: true,
      render: (item: Despesa) => {
        if (!item.dataDespesa) return '-';
        const date = new Date(item.dataDespesa.replace(' ', 'T'));
        return isNaN(date.getTime())
          ? item.dataDespesa
          : date.toLocaleDateString('pt-BR');
      },
    },
    {
      key: 'nomeUsuario' as keyof Despesa,
      header: 'Motorista',
      filterable: true,
      render: (item: Despesa) =>
        item.nomeUsuario || `#${item.codigoUsuario || '-'}`,
    },
    {
      key: 'descricaoCategoria' as keyof Despesa,
      header: 'Categoria',
      filterable: true,
    },
    {
      key: 'descricao' as keyof Despesa,
      header: 'Descrição',
      filterable: true,
    },
    {
      key: 'valor' as keyof Despesa,
      header: 'Valor',
      filterable: true,
      render: (item: Despesa) => formatCurrency(Number(item.valor)),
    },
    {
      key: 'codigoViagem' as keyof Despesa,
      header: 'Viagem',
      filterable: true,
      render: (item: Despesa) => item.codigoViagem || '-',
    },
    {
      key: 'status' as keyof Despesa,
      header: 'Status',
      filterable: true,
      render: (item: Despesa) => (
        <S.StatusBadge status={item.status}>{item.status}</S.StatusBadge>
      ),
    },
    {
      key: 'imagem' as keyof Despesa,
      header: 'Comprovante',
      render: (item: Despesa) =>
        item.imagem ? (
          <S.ActionLink onClick={() => handleViewImage(item)}>
            Visualizar
          </S.ActionLink>
        ) : (
          <span>Sem comprovante</span>
        ),
    },
  ];

  const entregadoresOptions: MultiSelectOption[] = entregadores.map((e) => ({
    value: e.codigousuario,
    label: e.nomeusuario,
  }));

  const categoriaOptions: MultiSelectOption[] = categorias.map((c) => ({
    value: c.codigo,
    label: c.descricao,
  }));

  const summary = useMemo(() => {
    const total = despesas.reduce((acc, d) => acc + Number(d.valor || 0), 0);
    const aprovadas = despesas.filter((d) => d.status === 'APROVADA');
    const pendentes = despesas.filter((d) => d.status === 'PENDENTE');
    const rejeitadas = despesas.filter((d) => d.status === 'REJEITADA');
    return {
      qtd: despesas.length,
      total,
      aprovado: aprovadas.reduce((a, d) => a + Number(d.valor || 0), 0),
      pendente: pendentes.reduce((a, d) => a + Number(d.valor || 0), 0),
      rejeitado: rejeitadas.reduce((a, d) => a + Number(d.valor || 0), 0),
    };
  }, [despesas]);

  const chartData = useMemo(() => {
    const map = new Map<string, number>();
    despesas.forEach((d) => {
      const label = d.descricaoCategoria || 'Sem categoria';
      map.set(label, (map.get(label) || 0) + Number(d.valor || 0));
    });
    return Array.from(map.entries())
      .map(([name, valor]) => ({ name, valor }))
      .sort((a, b) => b.valor - a.valor);
  }, [despesas]);

  const actions = useMemo(
    () => [
      {
        icon: faPlay,
        onClick: handleSearch,
        title: 'Buscar',
        children: 'Buscar',
        disabled: loading,
        backgroundColor: (theme: DefaultTheme) => theme.primaryColor,
      },
      {
        icon: faEraser,
        onClick: handleClear,
        title: 'Limpar',
        children: 'Limpar',
        backgroundColor: '#4dc9c9',
      },
    ],
    [handleSearch, loading, handleClear]
  );

  const customLegendFormatter = (value: string) => (
    <S.LegendText>{value}</S.LegendText>
  );

  return (
    <S.RelatoriosContainer>
      <S.RelatoriosContent>
        <S.FilterContainer>
          <S.FilterGroup>
            <DateInput
              id="dataInicio"
              setValue={(value) => handleInputChange('dataInicio', value)}
              initialValue={formData.dataInicio}
              hasLabel={true}
              label="Data Início"
              required={true}
              labelPosition="left"
            />
            <DateInput
              id="dataFim"
              setValue={(value) => handleInputChange('dataFim', value)}
              initialValue={formData.dataFim}
              hasLabel={true}
              label="Data Fim"
              required={true}
              labelPosition="left"
            />
          </S.FilterGroup>
        </S.FilterContainer>

        <S.FilterContainer>
          <S.FilterGroup>
            <MultiSelect
              id="usuario"
              options={entregadoresOptions}
              selected={formData.usuarios}
              onChange={(values) => handleInputChange('usuarios', values)}
              placeholder="Todos os entregadores"
            />
            <MultiSelect
              id="categoria"
              options={categoriaOptions}
              selected={formData.codigosCategoria}
              onChange={(values) =>
                handleInputChange(
                  'codigosCategoria',
                  values.map((v) => Number(v))
                )
              }
              placeholder="Todas as categorias"
            />
            <MultiSelect
              id="status"
              options={STATUS_OPTIONS}
              selected={formData.statuses}
              onChange={(values) =>
                handleInputChange('statuses', values as StatusDespesa[])
              }
              placeholder="Todos os status"
            />
          </S.FilterGroup>
        </S.FilterContainer>

        <S.ButtonContainer>
          <ActionButtons buttons={actions} />
        </S.ButtonContainer>

        {error && <S.ErrorContainer>{error}</S.ErrorContainer>}

        {despesas.length > 0 && (
          <>
            <S.SummaryContainer>
              <S.SummaryCard>
                <S.SummaryLabel>Qtd. Despesas</S.SummaryLabel>
                <S.SummaryValue>{summary.qtd}</S.SummaryValue>
              </S.SummaryCard>
              <S.SummaryCard>
                <S.SummaryLabel>Total</S.SummaryLabel>
                <S.SummaryValue>{formatCurrency(summary.total)}</S.SummaryValue>
              </S.SummaryCard>
              <S.SummaryCard>
                <S.SummaryLabel>Aprovado</S.SummaryLabel>
                <S.SummaryValue>
                  {formatCurrency(summary.aprovado)}
                </S.SummaryValue>
              </S.SummaryCard>
              <S.SummaryCard>
                <S.SummaryLabel>Pendente</S.SummaryLabel>
                <S.SummaryValue>
                  {formatCurrency(summary.pendente)}
                </S.SummaryValue>
              </S.SummaryCard>
              <S.SummaryCard>
                <S.SummaryLabel>Rejeitado</S.SummaryLabel>
                <S.SummaryValue>
                  {formatCurrency(summary.rejeitado)}
                </S.SummaryValue>
              </S.SummaryCard>
            </S.SummaryContainer>

            <S.ChartContainer>
              <S.ChartWrapper>
                <S.ChartTitle>Despesas por Categoria</S.ChartTitle>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-35}
                      textAnchor="end"
                      height={80}
                      tickMargin={10}
                      stroke={theme.textColor}
                    />
                    <YAxis
                      stroke={theme.textColor}
                      tickFormatter={(v: number) =>
                        new Intl.NumberFormat('pt-BR', {
                          notation: 'compact',
                          compactDisplay: 'short',
                        }).format(v)
                      }
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(125,125,125,0.1)' }}
                      contentStyle={{
                        backgroundColor: theme.white,
                        borderColor: theme.borderColor,
                        color: theme.textColor,
                      }}
                      labelStyle={{ color: theme.textColor }}
                      itemStyle={{ color: theme.textColor }}
                      formatter={(value: number) => [
                        formatCurrency(value),
                        'Total',
                      ]}
                    />
                    <Legend formatter={customLegendFormatter} />
                    <Bar
                      dataKey="valor"
                      name="Valor total"
                      fill={theme.primaryColor}
                    >
                      {chartData.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            CATEGORIA_COLORS[index % CATEGORIA_COLORS.length]
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </S.ChartWrapper>
            </S.ChartContainer>
          </>
        )}

        <S.TableContainer>
          {despesas.length > 0 ? (
            <Table data={despesas} columns={columns} exportable={true} />
          ) : (
            <S.NoDataContainer>
              {loading ? 'Carregando...' : 'Nenhuma despesa encontrada'}
            </S.NoDataContainer>
          )}
        </S.TableContainer>
      </S.RelatoriosContent>

      {showModal && selectedDespesa && (
        <Modal
          isOpen={true}
          onClose={closeModal}
          title={`Comprovante da Despesa #${selectedDespesa.codigoDespesa}`}
          headerIcon={faImage}
        >
          <S.ModalContainer>
            {selectedDespesa.imagem ? (
              <S.ModalImage
                src={selectedDespesa.imagem}
                alt="Comprovante da despesa"
              />
            ) : (
              <S.ImagePlaceholder>
                Sem comprovante disponível
              </S.ImagePlaceholder>
            )}
          </S.ModalContainer>
        </Modal>
      )}
    </S.RelatoriosContainer>
  );
};

export default RelatoriosDespesasContent;
